/*eslint max-lines-per-function: ["error", 250]*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Tab } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import { TabsHistory } from '../../../app/components/TabsHistory';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNavigation } from '../../../common/navigation';
import { useAuth } from '../../../common/hooks/useAuth';
import { Role } from '../../../common/spdCore/authorization/roles';
import {
  GetSalesInvoiceDto,
  GetSalesInvoiceDtoStatusEnum,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { SalesInvoiceBasicData } from '../../components/SalesInvoice/SalesInvoiceBasicData';
import { SalesInvoicePositions } from '../../components/SalesInvoice/SalesInvoicePositions';
import { SalesInvoiceFinalize } from '../../components/SalesInvoice/SalesInvoiceFinalize';
import { SalesInvoicePaymentLinks } from '../../components/SalesInvoice/SalesInvoicePaymentLinks';
import { SalesInvoiceExams } from '../../components/SalesInvoice/SalesInvoiceExams';
import { buildSalesInvoiceTubeTitle } from '../../components/SalesInvoice/buildSalesInvoiceTubeTitle';

export enum SalesInvoiceEditorTabs {
  BASIC_DATA = 'basicData',
  EXAMS = 'exams',
  POSITIONS = 'positions',
  FINALIZE = 'finalize',
  PAYMENTS = 'payments',
}

interface TabProps {
  key: SalesInvoiceEditorTabs;
  title: string;
  component: React.ReactNode;
  disabled: boolean;
  notSaved?: boolean;
}

const getTabClassName = (notSaved?: boolean) =>
  notSaved ? 'not-saved-tab' : '';

type ParamTypes = {
  salesInvoiceId: string;
};

export const SalesInvoiceEditorView: React.FC = () => {
  const { salesInvoiceId: salesInvoiceIdFromParams } = useParams<ParamTypes>();
  const spd = useSpdCore();
  const nav = useNavigation();
  const auth = useAuth();

  const [notSavedTab, setNotSavedTab] = useState<
    SalesInvoiceEditorTabs | undefined
  >(undefined);
  const [salesInvoiceId, setSalesInvoiceId] = useState<string | undefined>(
    salesInvoiceIdFromParams,
  );
  const [salesInvoice, setSalesInvoice] = useState<
    GetSalesInvoiceDto | undefined
  >(undefined);
  const [activeTab, setActiveTab] = useState<string>(
    SalesInvoiceEditorTabs.BASIC_DATA,
  );
  const [paymentsRefreshKey, setPaymentsRefreshKey] = useState(false);
  const [positionsRefreshKey, setPositionsRefreshKey] = useState(false);

  useEffect(() => {
    setSalesInvoiceId(salesInvoiceIdFromParams);
    setSalesInvoice(undefined);
  }, [salesInvoiceIdFromParams]);

  const loadSalesInvoice = useCallback((): Promise<void> => {
    if (!salesInvoiceId) {
      return Promise.resolve();
    }

    return spd.salesInvoices
      .getById(salesInvoiceId)
      .then(salesInvoiceData => {
        setSalesInvoice(salesInvoiceData);
        setPaymentsRefreshKey(k => !k);
      })
      .catch(errorResponse => {
        console.error(errorResponse);
      });
  }, [salesInvoiceId, spd.salesInvoices]);

  useEffect(() => {
    if (salesInvoiceId) {
      void loadSalesInvoice();
    }
  }, [salesInvoiceId, loadSalesInvoice]);

  const handleExamsChanged = useCallback(async () => {
    await loadSalesInvoice();
    setPositionsRefreshKey(k => !k);
    setNotSavedTab(undefined);
  }, [loadSalesInvoice]);

  const isReadOnly = useMemo(() => {
    if (!salesInvoice) {
      return false;
    }
    if (
      salesInvoice.status !== GetSalesInvoiceDtoStatusEnum.Draft ||
      !salesInvoice.active
    ) {
      return true;
    }
    const isCorrection = !!salesInvoice.originalInvoice?.id;
    if (isCorrection) {
      return !auth.check([Role.Admin, Role.Finance]);
    }
    return !auth.check([Role.Admin, Role.Finance, Role.BasicFinance]);
  }, [salesInvoice, auth]);

  const tabs: TabProps[] = useMemo(() => {
    return [
      {
        key: SalesInvoiceEditorTabs.BASIC_DATA,
        title: 'Dane podstawowe',
        component: (
          <SalesInvoiceBasicData
            salesInvoice={salesInvoice}
            salesInvoiceId={salesInvoiceId}
            setSalesInvoiceId={setSalesInvoiceId}
            setNotSavedTab={setNotSavedTab}
            setActiveTab={setActiveTab}
            refreshSalesInvoice={async () => {
              await loadSalesInvoice();
            }}
            readOnly={isReadOnly}
          />
        ),
        disabled: false,
        notSaved: notSavedTab === SalesInvoiceEditorTabs.BASIC_DATA,
      },
      {
        key: SalesInvoiceEditorTabs.EXAMS,
        title: 'Badania',
        component: (
          <SalesInvoiceExams
            salesInvoiceId={salesInvoiceId}
            onExamsChanged={handleExamsChanged}
            readOnly={isReadOnly}
          />
        ),
        disabled: !salesInvoiceId,
      },
      {
        key: SalesInvoiceEditorTabs.POSITIONS,
        title: 'Pozycje',
        component: (
          <SalesInvoicePositions
            salesInvoice={salesInvoice}
            id={salesInvoiceId}
            handleRefresh={loadSalesInvoice}
            setNotSavedTab={setNotSavedTab}
            setActiveTab={setActiveTab}
            readOnly={isReadOnly}
            positionsRefreshKey={positionsRefreshKey}
          />
        ),
        disabled: !salesInvoiceId,
        notSaved: notSavedTab === SalesInvoiceEditorTabs.POSITIONS,
      },
      {
        key: SalesInvoiceEditorTabs.FINALIZE,
        title: 'Podsumowanie',
        component: (
          <SalesInvoiceFinalize
            salesInvoice={salesInvoice}
            salesInvoiceId={salesInvoiceId}
            handleRefresh={loadSalesInvoice}
            setNotSavedTab={setNotSavedTab}
            readOnly={isReadOnly}
          />
        ),
        disabled: !salesInvoiceId,
        notSaved: notSavedTab === SalesInvoiceEditorTabs.FINALIZE,
      },
      {
        key: SalesInvoiceEditorTabs.PAYMENTS,
        title: 'Wpłaty',
        component: (
          <SalesInvoicePaymentLinks
            salesInvoiceId={salesInvoiceId}
            amountGross={salesInvoice?.amountGross}
            refreshKey={paymentsRefreshKey}
          />
        ),
        disabled: !salesInvoiceId,
      },
    ];
  }, [
    salesInvoice,
    salesInvoiceId,
    notSavedTab,
    loadSalesInvoice,
    handleExamsChanged,
    isReadOnly,
    paymentsRefreshKey,
    positionsRefreshKey,
  ]);

  const handlePrint = async () => {
    if (!salesInvoice?.id) {
      return;
    }
    try {
      await spd.pdfPrinter.openInvoice(salesInvoice.id);
    } catch (error) {
      console.error(error);
    }
  };

  const handlePreview = async () => {
    if (!salesInvoice?.id) {
      return;
    }
    try {
      await spd.pdfPrinter.previewInvoice(salesInvoice.id);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Card className="main-card">
        <Card.Body className="m-3">
          <Card.Title className="pb-3">
            {buildSalesInvoiceTubeTitle(
              salesInvoice,
              handlePrint,
              handlePreview,
              id => nav.salesInvoiceEditor(id, true),
            )}
          </Card.Title>
          <TabsHistory
            activeKey={activeTab}
            setActiveTab={setActiveTab}
            id="sales-invoice-editor"
            className="my-3"
          >
            {tabs.map(({ key, title, component, notSaved, disabled }) => (
              <Tab
                key={key}
                eventKey={key}
                tabClassName={getTabClassName(notSaved)}
                title={`${title} ${notSaved ? '(NIEZAPISANE)' : ''}`}
                className="m-3"
                disabled={disabled}
              >
                {component}
              </Tab>
            ))}
          </TabsHistory>
        </Card.Body>
      </Card>
      <div className="mt-3 ms-3 d-flex gap-2">
        {auth.check([Role.Admin, Role.Finance]) && (
          <Button
            variant="outline-secondary"
            onClick={() => nav.salesInvoices()}
          >
            Przejdź do listy faktur
          </Button>
        )}
        {salesInvoice?.examIds?.length === 1 &&
          auth.check([
            Role.PsychologistAssessor,
            Role.PsychologistSpecialist,
            Role.MedicalAssessor,
            Role.MedicalRegistration,
            Role.Office,
            Role.ExtendedRegistration,
          ]) && (
            <Button
              variant="outline-secondary"
              onClick={() => nav.examTests(salesInvoice.examIds?.[0] ?? '')}
            >
              Przejdź do badania
            </Button>
          )}
      </div>
    </>
  );
};
